<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { Close } from "@element-plus/icons-vue";
import { useUserStore } from "@/stores/user";
import mapImg from "@/assets/img/dashboard/growth-system/map.png";
import goalImg from "@/assets/img/dashboard/growth-system/goal.png";
import redPacketImg from "@/assets/img/dashboard/growth-system/red-packet.png";

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const {
  isGrowthSystemTestGroup,
  growthSystemLevel,
  growthSystemUnclaimedPoints,
  growthSystemTaskListLength,
  growthSystemTaskListCompletedLen,
} = storeToRefs(userStore);

const isShow = ref(true);
const notClickClose = ref(true);
const shakeRef = ref(null);
let timer = null;
let timer2 = null;

/** Vue2: isGrowthSystemTestGroup && path 不含 growthCenter / home */
const visible = computed(
  () =>
    isGrowthSystemTestGroup.value &&
    isShow.value &&
    !String(route.path).includes("growthCenter") &&
    !String(route.path).includes("home")
);

const showRedPacket = computed(
  () => growthSystemLevel.value && growthSystemUnclaimedPoints.value > 0 && notClickClose.value
);

const showGoal = computed(() => !!growthSystemLevel.value && !showRedPacket.value);

const enterLabel = computed(() =>
  growthSystemUnclaimedPoints.value > 0 && notClickClose.value ? "Receive" : "Enter"
);

function clearTimers() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  if (timer2) {
    clearTimeout(timer2);
    timer2 = null;
  }
}

function startShake() {
  if (!shakeRef.value) return;
  const shouldShake = !growthSystemLevel.value || showRedPacket.value;
  if (!shouldShake) return;
  shakeRef.value.classList.add("shake");
  timer = setInterval(() => {
    if (!shakeRef.value) return;
    shakeRef.value.classList.add("shake");
    timer2 = setTimeout(() => {
      shakeRef.value?.classList.remove("shake");
    }, 2000);
  }, 12000);
}

function close() {
  clearTimers();
  if (growthSystemUnclaimedPoints.value > 0 && notClickClose.value) {
    notClickClose.value = false;
    return;
  }
  isShow.value = false;
}

function goGrowth() {
  router.push("/growthCenter");
}

watch([visible, showRedPacket, growthSystemLevel], async () => {
  clearTimers();
  if (!visible.value) return;
  await nextTick();
  startShake();
});

onMounted(() => {
  userStore.fetchTutorHomeTask();
});

onBeforeUnmount(() => {
  clearTimers();
});
</script>

<template>
  <!-- 图2：纵向 — 图标 → (n/m) → Enter；右上角圆形关闭 -->
  <div v-if="visible" class="growth-entry-wrap">
    <button type="button" class="close-btn" aria-label="close" @click="close">
      <el-icon :size="12"><Close /></el-icon>
    </button>

    <div class="entry">
      <img v-if="!growthSystemLevel" ref="shakeRef" class="entry-icon" :src="mapImg" alt="" />
      <img v-else-if="showRedPacket" ref="shakeRef" class="entry-icon" :src="redPacketImg" alt="" />
      <img v-else class="entry-icon" :src="goalImg" alt="" />
    </div>

    <p v-if="showGoal" class="progress-label">
      ({{ growthSystemTaskListCompletedLen }}/{{ growthSystemTaskListLength }})
    </p>

    <button type="button" class="enter-btn" @click="goGrowth">{{ enterLabel }}</button>
  </div>
</template>

<style scoped lang="scss">
/* Vue2 .overall-btns-right — 右下角浮动 */
.growth-entry-wrap {
  position: fixed;
  right: 22px;
  bottom: 70px;
  z-index: 199;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 120px;
  text-align: center;
}

.entry {
  position: relative;
  line-height: 0;
}

.entry-icon {
  display: block;
  width: 56px;
  height: auto;
  margin: 0 auto;
}

.progress-label {
  margin: 4px 0 0;
  padding: 0;
  font-size: 13px;
  line-height: 18px;
  color: #32397a;
  font-weight: 500;
}

.close-btn {
  position: absolute;
  top: -24px;
  right: 4px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #dcdcdc;
  color: #000;
  cursor: pointer;
  line-height: 1;

  &:hover {
    background: #d8d8de;
  }

  :deep(.el-icon) {
    color: #000;
  }
}

/* Vue2 pink-button + round */
.enter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 6px;
  height: 24px;
  min-width: 72px;
  padding: 0 14px;
  border: none;
  border-radius: 6px;
  background: #ff9991;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  line-height: 24px;
  cursor: pointer;
  box-shadow: none;

  &:hover {
    opacity: 0.92;
  }
}

.shake {
  animation: shaky-slow 2s 1;
}

@keyframes shaky-slow {
  0% {
    transform: translate(0px, 0px) rotate(0deg);
  }
  2% {
    transform: translate(-1px, 1.5px) rotate(1.5deg);
  }
  4% {
    transform: translate(1.3px, 0px) rotate(-0.5deg);
  }
  6% {
    transform: translate(1.4px, 1.4px) rotate(-2deg);
  }
  8% {
    transform: translate(-1.3px, -1px) rotate(-1.5deg);
  }
  10% {
    transform: translate(1.4px, 0px) rotate(-2deg);
  }
  20% {
    transform: translate(1px, 1px) rotate(-0.5deg);
  }
  30% {
    transform: translate(-1.3px, -1.3px) rotate(-1.5deg);
  }
  40% {
    transform: translate(-1.4px, -1px) rotate(-0.5deg);
  }
  50% {
    transform: translate(1.6px, 1.6px) rotate(1.5deg);
  }
  60% {
    transform: translate(1.3px, 1.6px) rotate(-0.5deg);
  }
  70% {
    transform: translate(-1.6px, 1px) rotate(1.5deg);
  }
  80% {
    transform: translate(1.4px, 1.6px) rotate(2deg);
  }
  90% {
    transform: translate(-1.6px, -1.6px) rotate(-2deg);
  }
  100% {
    transform: translate(0px, 0px) rotate(0deg);
  }
}
</style>

<style lang="scss">
[data-theme="theme1"] .growth-entry-wrap .progress-label {
  color: #ececec;
}
</style>
